import React, { useEffect, useState } from "react";
import axios from "axios";
import Topbar from "./Topbar";
import Sidebar from "./Sidebar";
import Alert from "./Alert";
import "../App.css";

export default function UserManager()
{
  const [user] = useState(() =>
  {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState({ type: "", message: "" });

  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");

  const [newDashboardTitle, setNewDashboardTitle] = useState("");
  const [newDashboardReportId, setNewDashboardReportId] = useState("");

  const [selectedUser, setSelectedUser] = useState("");
  const [selectedDashboard, setSelectedDashboard] = useState("");

  // 🔹 Load Users and Dashboards (with assignments)
  useEffect(() =>
  {
    if (!user)
    {
      window.location.href = "/login";
      return;
    }

    const fetchUsersAndAssignments = async () =>
    {
      try
      {
        const usersRes = await axios.get("https://bidashboard.miplapp.in/backend/api/users.php");
        const dashboardsRes = await axios.get("https://bidashboard.miplapp.in/backend/api/dashboards.php");
        const assignmentsRes = await axios.get("https://bidashboard.miplapp.in/backend/api/user-dashboard-assignments.php");

        const usersData = usersRes.data;
        const assignments = assignmentsRes.data;

        const usersWithDashboards = usersData.map(u => ({
          ...u,
          assignedDashboards: assignments
            .filter(a => a.user_id === u.id)
            .map(a => ({ id: a.dashboard_id, title: a.title }))
        }));

        setUsers(usersWithDashboards);
        setDashboards(dashboardsRes.data);
      } catch (err)
      {
        console.error("Failed to load data:", err);
        setAlertMsg({ type: "error", message: "Failed to load user data." });
      }
    };

    fetchUsersAndAssignments();
  }, [user]);

  // 🔹 Create User
  const createUser = async () =>
  {
    if (!newUsername || !newPassword)
    {
      setAlertMsg({ type: "error", message: "Username and password are required." });
      return;
    }
    setLoading(true);
    try
    {
      await axios.post("https://bidashboard.miplapp.in/backend/api/register.php", {
        username: newUsername,
        password: newPassword,
        role: newRole,
      });
      setAlertMsg({ type: "success", message: "User created successfully!" });
      setNewUsername("");
      setNewPassword("");
      setNewRole("user");

      const res = await axios.get("https://bidashboard.miplapp.in/backend/api/users.php");
      setUsers(res.data);
    } catch (err)
    {
      console.error(err);
      setAlertMsg({ type: "error", message: err.response?.data?.message || "Failed to create user" });
    } finally
    {
      setLoading(false);
    }
  };

  // 🔹 Create Dashboard
  const createDashboard = async () =>
  {
    if (!newDashboardTitle)
    {
      setAlertMsg({ type: "error", message: "Dashboard title is required." });
      return;
    }
    setLoading(true);
    try
    {
      await axios.post("https://bidashboard.miplapp.in/backend/api/create-dashboard.php", {
        title: newDashboardTitle,
        reportId: newDashboardReportId || null,
      });
      setAlertMsg({ type: "success", message: "Dashboard created successfully!" });
      setNewDashboardTitle("");
      setNewDashboardReportId("");

      const res = await axios.get("https://bidashboard.miplapp.in/backend/api/dashboards.php");
      setDashboards(res.data);
    } catch (err)
    {
      console.error(err);
      setAlertMsg({ type: "error", message: "Failed to create dashboard" });
    } finally
    {
      setLoading(false);
    }
  };

  // 🔹 Assign Dashboard
  const assignDashboard = async () =>
  {
    if (!selectedUser || !selectedDashboard)
    {
      setAlertMsg({ type: "error", message: "Please select a user and dashboard." });
      return;
    }
    setLoading(true);
    try
    {
      const res = await axios.post("https://bidashboard.miplapp.in/backend/api/assign-dashboard.php", {
        userId: selectedUser,
        dashboardId: selectedDashboard,
      });

      if (res.data.status === "error")
      {
        setAlertMsg({ type: "error", message: res.data.message });
      } else
      {
        setAlertMsg({ type: "success", message: res.data.message });
        const dashboard = dashboards.find(d => d.id === parseInt(selectedDashboard));
        setUsers(users.map(u =>
          u.id === parseInt(selectedUser)
            ? { ...u, assignedDashboards: [...(u.assignedDashboards || []), dashboard] }
            : u
        ));
      }

      setSelectedUser("");
      setSelectedDashboard("");
    } catch (err)
    {
      console.error(err);
      setAlertMsg({ type: "error", message: "Failed to assign dashboard" });
    } finally
    {
      setLoading(false);
    }
  };

  // 🔹 Remove User
  const removeUser = async (userId) =>
  {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    setLoading(true);
    try
    {
      const res = await axios.post("https://bidashboard.miplapp.in/backend/api/remove-user.php", { userId });
      if (res.data.status === "success")
      {
        setAlertMsg({ type: "success", message: res.data.message });

        const usersRes = await axios.get("https://bidashboard.miplapp.in/backend/api/users.php");
        setUsers(usersRes.data);
      } else
      {
        setAlertMsg({ type: "error", message: res.data.message || "Failed to remove user" });
      }
    } catch (err)
    {
      console.error(err);
      setAlertMsg({ type: "error", message: "Failed to remove user" });
    } finally
    {
      setLoading(false);
    }
  };

  // 🔹 Remove Dashboard Assignment (updated to use backend return)
  const removeDashboardAssignment = async (userId, dashboardId) =>
  {
    if (!window.confirm("Are you sure you want to remove this dashboard from this user?")) return;
    try
    {
      const res = await axios.post("https://bidashboard.miplapp.in/backend/api/remove-dashboard-assignment.php", { userId, dashboardId });

      if (res.data.status === "success")
      {
        setAlertMsg({ type: "success", message: res.data.message });

        const updatedDashboards = res.data.updatedDashboards || [];
        setUsers(users.map(u =>
          u.id === userId ? { ...u, assignedDashboards: updatedDashboards } : u
        ));
      } else
      {
        setAlertMsg({ type: "error", message: res.data.message || "Failed to remove dashboard" });
      }
    } catch (err)
    {
      console.error(err);
      setAlertMsg({ type: "error", message: "Failed to remove dashboard" });
    }
  };

  // 🔹 Remove Dashboard
  const removeDashboard = async (dashboardId) =>
  {
    if (!window.confirm("Are you sure you want to delete this dashboard?")) return;

    setLoading(true);
    try
    {
      const res = await axios.post("https://bidashboard.miplapp.in/backend/api/remove-dashboard.php", { dashboardId });
      if (res.data.status === "success")
      {
        setAlertMsg({ type: "success", message: res.data.message });

        const dashboardsRes = await axios.get("https://bidashboard.miplapp.in/backend/api/dashboards.php");
        setDashboards(dashboardsRes.data);
      } else
      {
        setAlertMsg({ type: "error", message: res.data.message || "Failed to remove dashboard" });
      }
    } catch (err)
    {
      console.error(err);
      setAlertMsg({ type: "error", message: "Failed to remove dashboard" });
    } finally
    {
      setLoading(false);
    }
  };


  // 🔹 UI Rendering
  return (
    <>
      <Topbar onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} collapsed={sidebarCollapsed} />
      <Sidebar collapsed={sidebarCollapsed} role={user?.role} />
      <div className={`main-content modern-admin ${sidebarCollapsed ? "collapsed" : ""}`}>

        <div className="welcome-banner" style={{ marginBottom: "20px" }}>
          <h2>👋 Welcome, {user?.username}</h2>
          <p>Manage users and dashboards below</p>
        </div>

        <Alert type={alertMsg.type} message={alertMsg.message} onClose={() => setAlertMsg({ type: "", message: "" })} />

        <div className="user-manager-container" style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>

          {/* 🔹 Create User Card */}
          <div className="user-manager-card" style={{ flex: "1", minWidth: "280px", padding: "33px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", background: "#fff" }}>
            <h2>➕ Create New User</h2>
            <div className="form-row">
              <label>Username</label>
              <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="Enter username" />
            </div>
            <div className="form-row">
              <label>Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter password" />
            </div>
            <div className="form-row">
              <label>Role</label>
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button className="assign-btn" onClick={createUser} disabled={loading} style={{ marginTop: "10px", padding: "8px 16px", background: "#1890ff", color: "#fff", borderRadius: "5px", border: "none", cursor: "pointer" }}>
              {loading ? "Creating..." : "Create User"}
            </button>
          </div>

          {/* 🔹 Create Dashboard Card */}
          <div className="user-manager-card" style={{ flex: "1", minWidth: "400px", padding: "32px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", background: "#fff" }}>
            <h2>➕ Create New Dashboard</h2>
            <div className="form-row">
              <label>Dashboard Title</label>
              <input type="text" value={newDashboardTitle} onChange={(e) => setNewDashboardTitle(e.target.value)} placeholder="Enter dashboard title" />
            </div>
            <div className="form-row">
              <label>Power BI Report ID</label>
              <input type="text" value={newDashboardReportId} onChange={(e) => setNewDashboardReportId(e.target.value)} placeholder="Enter Report ID" />
            </div>
            <button className="assign-btn" onClick={createDashboard} disabled={loading} style={{ marginTop: "95px", padding: "8px 16px", background: "#52c41a", color: "#fff", borderRadius: "5px", border: "none", cursor: "pointer" }}>
              {loading ? "Creating..." : "Create Dashboard"}
            </button>
          </div>

          {/* 🔹 Assign Dashboard Card */}
          <div className="user-manager-card" style={{ flex: "1", minWidth: "280px", padding: "32px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", background: "#fff" }}>
            <h2>👥 Assign Dashboard</h2>
            <div className="form-row">
              <label>User</label>
              <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                <option value="">Select User</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>Dashboard</label>
              <select value={selectedDashboard} onChange={(e) => setSelectedDashboard(e.target.value)}>
                <option value="">Select Dashboard</option>
                {dashboards.map(d => (
                  <option key={d.id} value={d.id}>{d.title}</option>
                ))}
              </select>
            </div>
            <button className="assign-btn" onClick={assignDashboard} disabled={loading} style={{ marginTop: "95px", padding: "8px 16px", background: "#faad14", color: "#fff", borderRadius: "5px", border: "none", cursor: "pointer" }}>
              {loading ? "Assigning..." : "Assign Dashboard"}
            </button>
          </div>
          {/* 🔹 Dashboard List Card */}
          <div className="user-manager-card" style={{ flex: "1", minWidth: "400px", padding: "20px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", background: "#fff" }}>
            <h2>📊 Existing Dashboards</h2>
            {dashboards.length === 0 ? (
              <p>No dashboards created yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
                <thead>
                  <tr>
                    <th style={{ borderBottom: "1px solid #ccc", padding: "8px" }}>Title</th>
                    <th style={{ borderBottom: "1px solid #ccc", padding: "8px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboards.map(d => (
                    <tr key={d.id}>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>{d.title}</td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        <button
                          onClick={() => removeDashboard(d.id)}
                          style={{ background: "#ff4d4f", color: "#fff", border: "none", padding: "5px 12px", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* 🔹 Users Table */}
          <div
            className="user-manager-card"
            style={{
              flex: "2",
              minWidth: "700px",
              padding: "25px",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              background: "#fff",
            }}
          >
            <h2
              style={{
                marginBottom: "15px",
                color: "#0078D4",
                borderBottom: "2px solid #E6F0FA",
                paddingBottom: "5px",
                fontWeight: "600",
              }}
            >
              Users
            </h2>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Segoe UI, sans-serif",
                fontSize: "15px",
              }}
            >
              <thead>
                <tr style={{ background: "#F9FAFB" }}>
                  <th
                    style={{
                      textAlign: "left",
                      borderBottom: "2px solid #E0E0E0",
                      padding: "10px 8px",
                      color: "#555",
                      fontWeight: "600",
                    }}
                  >
                    Username
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      borderBottom: "2px solid #E0E0E0",
                      padding: "10px 8px",
                      color: "#555",
                      fontWeight: "600",
                    }}
                  >

                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      borderBottom: "2px solid #E0E0E0",
                      padding: "10px 8px",
                      color: "#555",
                      fontWeight: "600",
                    }}
                  >
                    Dashboards
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      borderBottom: "2px solid #E0E0E0",
                      padding: "10px 8px",
                      color: "#555",
                      fontWeight: "600",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      background: "#fff",
                      transition: "background 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                  >
                    <td style={{ padding: "10px 8px", fontWeight: "500", color: "#333" }}>
                      {u.username}
                    </td>
                    <td style={{ padding: "10px 8px", color: "#666" }}>{u.role}</td>
                    <td style={{ padding: "10px 8px" }}>
                      {u.assignedDashboards?.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {u.assignedDashboards.map((d) => (
                            <span
                              key={d.id}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                background: "#E6F4FF",
                                padding: "5px 10px",
                                borderRadius: "20px",
                                color: "#0056b3",
                                fontSize: "13px",
                              }}
                            >
                              {d.title}
                              <button
                                onClick={() => removeDashboardAssignment(u.id, d.id)}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "#d9534f",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  lineHeight: "1",
                                }}
                                title="Remove Dashboard"
                              >
                                ❌
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <em style={{ color: "#aaa" }}>No dashboards assigned</em>
                      )}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center" }}>
                      <button
                        onClick={() => removeUser(u.id)}
                        style={{
                          background: "#ff4d4f",
                          color: "#fff",
                          border: "none",
                          padding: "6px 14px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "500",
                          transition: "background 0.2s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#e04344")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#ff4d4f")}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
